import { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { MdAdd, MdEdit, MdDelete, MdSecurity, MdToggleOn, MdSettings } from 'react-icons/md';
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { formatDistanceToNow } from 'date-fns';
import { useOutlet } from '../contexts/OutletContext';

// Helper function to safely convert Firestore timestamps to Date objects
const toDate = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp.toDate) return timestamp.toDate(); // Firestore Timestamp
  if (timestamp instanceof Date) return timestamp; // Already a Date
  if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    return new Date(timestamp); // Convert string or number to Date
  }
  return null;
};

const defaultPolicy = {
  name: '',
  description: '',
  targetType: 'global',
  targetId: '',
  priority: 0,
  isActive: true,
  security: {
    requirePin: false,
    requireBiometric: false,
    autoLockMinutes: 5,
    allowDebugMode: false,
    requireDeviceEncryption: true,
    allowScreenCapture: false,
  },
  deviceFeatures: {
    allowBluetooth: true,
    allowCamera: true,
    allowWiFi: true,
    allowGPS: true,
    allowNFC: true,
    allowUSB: false,
  },
  kioskMode: {
    enabled: false,
    allowedApps: [],
    hideStatusBar: false,
    hideNavigationBar: false,
    disableHomeButton: false,
  },
  updates: {
    autoUpdate: true,
    updateChannel: 'stable',
    allowBetaFeatures: false,
    maintenanceWindow: 'night',
  },
  deviceRestrictions: {
    allowFactoryReset: false,
    allowUSBDebugging: false,
    allowAppInstallation: false,
    allowUninstallApps: false,
  },
};

function PoliciesView() {
  const { selectedOutlet } = useOutlet();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentPolicy, setCurrentPolicy] = useState(defaultPolicy);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!selectedOutlet) {
      console.log('[PoliciesView] No outlet selected');
      setLoading(false);
      return;
    }

    console.log('[PoliciesView] Subscribing to policies for outlet:', selectedOutlet.id);
    setLoading(true);

    // Query all policies and filter client-side to avoid composite index requirement
    const q = query(
      collection(db, 'device_policies')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const allPolicies = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Filter for policies that target this outlet or are global
        const policyList = allPolicies
          .filter(policy => 
            !policy.targetOutlets || 
            policy.targetOutlets.length === 0 || 
            policy.targetOutlets.includes(selectedOutlet.id)
          )
          .sort((a, b) => (b.priority || 0) - (a.priority || 0));
        
        console.log('[PoliciesView] Received policies:', policyList.length);
        setPolicies(policyList);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching policies:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [selectedOutlet]);

  const handleOpenDialog = (policy = null) => {
    if (policy) {
      setCurrentPolicy(policy);
      setIsEditing(true);
    } else {
      setCurrentPolicy({
        ...defaultPolicy,
        targetOutlets: selectedOutlet ? [selectedOutlet.id] : [],
      });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentPolicy(defaultPolicy);
    setIsEditing(false);
  };

  const handleSavePolicy = async () => {
    try {
      if (isEditing) {
        const policyRef = doc(db, 'device_policies', currentPolicy.id);
        await updateDoc(policyRef, {
          ...currentPolicy,
          updatedAt: serverTimestamp(),
        });
      } else {
        // Ensure targetOutlets is set for new policies
        const policyData = {
          ...currentPolicy,
          targetOutlets: currentPolicy.targetOutlets || (selectedOutlet ? [selectedOutlet.id] : []),
          enabled: currentPolicy.isActive,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: 'admin', // TODO: Replace with actual user
        };
        await addDoc(collection(db, 'device_policies'), policyData);
      }
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving policy:', err);
      alert('Error saving policy: ' + err.message);
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'device_policies', policyId));
    } catch (err) {
      console.error('Error deleting policy:', err);
      alert('Error deleting policy: ' + err.message);
    }
  };

  const handleFieldChange = (field, value) => {
    setCurrentPolicy((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedFieldChange = (section, field, value) => {
    setCurrentPolicy((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Error loading policies: {error}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0 fw-bold">
          <MdSecurity className="me-2" />
          Device Policies
        </h2>
        <button className="btn btn-primary" onClick={() => handleOpenDialog()}>
          <MdAdd className="me-1" />
          Create Policy
        </button>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover table-bordered">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Target</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Updates</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {policies.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      No policies found. Create your first policy to get started.
                    </td>
                  </tr>
                ) : (
                  policies.map((policy) => (
                    <tr key={policy.id}>
                      <td>
                        <strong>{policy.name}</strong>
                        <br />
                        <small className="text-muted">{policy.description}</small>
                      </td>
                      <td>
                        <span className={`badge rounded-pill ${
                          policy.targetType === 'global' ? 'bg-primary' :
                          policy.targetType === 'outlet' ? 'bg-secondary' : 'bg-info'
                        }`}>
                          {policy.targetType}
                        </span>
                        {policy.targetId && (
                          <>
                            <br />
                            <small>{policy.targetId}</small>
                          </>
                        )}
                      </td>
                      <td>{policy.priority}</td>
                      <td>
                        <span className={`badge rounded-pill ${policy.isActive ? 'bg-success' : 'bg-secondary'}`}>
                          {policy.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <span className={`badge ${
                            policy.updates?.updateChannel === 'stable' ? 'bg-success' :
                            policy.updates?.updateChannel === 'beta' ? 'bg-warning text-dark' : 'bg-info'
                          }`}>
                            {policy.updates?.updateChannel === 'stable' ? '🟢 Stable' :
                             policy.updates?.updateChannel === 'beta' ? '🟡 Beta' : '🔵 Dev'}
                          </span>
                          {policy.updates?.autoUpdate && (
                            <small className="text-muted">
                              <MdToggleOn className="text-success" /> Auto-update
                            </small>
                          )}
                        </div>
                      </td>
                      <td>
                        <small className="text-muted">
                          {toDate(policy.createdAt)
                            ? formatDistanceToNow(toDate(policy.createdAt), { addSuffix: true })
                            : 'N/A'}
                        </small>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleOpenDialog(policy)}
                        >
                          <MdEdit />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeletePolicy(policy.id)}
                        >
                          <MdDelete />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bootstrap Modal for Adding/Editing */}
      <Modal show={openDialog} onHide={handleCloseDialog} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Edit Policy' : 'Create New Policy'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Policy Name *</label>
            <input
              type="text"
              className="form-control"
              value={currentPolicy.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              value={currentPolicy.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              rows={2}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Target Type</label>
            <select
              className="form-select"
              value={currentPolicy.targetType}
              onChange={(e) => handleFieldChange('targetType', e.target.value)}
            >
              <option value="global">Global</option>
              <option value="outlet">Outlet</option>
              <option value="terminal">Terminal</option>
            </select>
          </div>

          {currentPolicy.targetType !== 'global' && (
            <div className="mb-3">
              <label className="form-label">Target ID</label>
              <input
                type="text"
                className="form-control"
                value={currentPolicy.targetId}
                onChange={(e) => handleFieldChange('targetId', e.target.value)}
              />
              <small className="form-text text-muted">Enter {currentPolicy.targetType} ID</small>
            </div>
          )}

          <div className="mb-3">
            <label className="form-label">Priority</label>
            <input
              type="number"
              className="form-control"
              value={currentPolicy.priority}
              onChange={(e) => handleFieldChange('priority', parseInt(e.target.value))}
            />
            <small className="form-text text-muted">Higher priority policies override lower ones</small>
          </div>

          <div className="mb-3">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="activeSwitch"
                checked={currentPolicy.isActive}
                onChange={(e) => handleFieldChange('isActive', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="activeSwitch">
                Active
              </label>
            </div>
          </div>

          <hr />

          <h6 className="mb-3">
            <MdSecurity className="me-2" />
            Security Settings
          </h6>
          
          <div className="mb-2">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="requirePin"
                checked={currentPolicy.security.requirePin}
                onChange={(e) => handleNestedFieldChange('security', 'requirePin', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="requirePin">
                Require PIN for App Access
              </label>
            </div>
          </div>

          <div className="mb-2">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="requireBiometric"
                checked={currentPolicy.security.requireBiometric}
                onChange={(e) => handleNestedFieldChange('security', 'requireBiometric', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="requireBiometric">
                Require Biometric Authentication
              </label>
            </div>
          </div>

          <div className="mb-2">
            <label className="form-label">Auto-Lock After (minutes)</label>
            <input
              type="number"
              className="form-control"
              value={currentPolicy.security.autoLockMinutes}
              onChange={(e) => handleNestedFieldChange('security', 'autoLockMinutes', parseInt(e.target.value) || 0)}
              min="0"
              max="60"
            />
            <small className="form-text text-muted">0 = disabled</small>
          </div>

          <div className="mb-2">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="allowDebugMode"
                checked={currentPolicy.security.allowDebugMode}
                onChange={(e) => handleNestedFieldChange('security', 'allowDebugMode', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="allowDebugMode">
                Allow Debug Mode (Developer Options)
              </label>
            </div>
          </div>

          <div className="mb-2">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="requireDeviceEncryption"
                checked={currentPolicy.security.requireDeviceEncryption}
                onChange={(e) => handleNestedFieldChange('security', 'requireDeviceEncryption', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="requireDeviceEncryption">
                Require Device Encryption
              </label>
            </div>
          </div>

          <div className="mb-3">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="allowScreenCapture"
                checked={currentPolicy.security.allowScreenCapture}
                onChange={(e) => handleNestedFieldChange('security', 'allowScreenCapture', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="allowScreenCapture">
                Allow Screen Capture / Screenshots
              </label>
            </div>
          </div>

          <hr />

          <h6 className="mb-3">
            <MdToggleOn className="me-2" />
            Device Features
          </h6>

          <div className="row">
            <div className="col-md-6">
              <div className="mb-2">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="allowBluetooth"
                    checked={currentPolicy.deviceFeatures?.allowBluetooth ?? true}
                    onChange={(e) => handleNestedFieldChange('deviceFeatures', 'allowBluetooth', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="allowBluetooth">
                    Allow Bluetooth
                  </label>
                </div>
              </div>

              <div className="mb-2">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="allowCamera"
                    checked={currentPolicy.deviceFeatures?.allowCamera ?? true}
                    onChange={(e) => handleNestedFieldChange('deviceFeatures', 'allowCamera', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="allowCamera">
                    Allow Camera
                  </label>
                </div>
              </div>

              <div className="mb-2">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="allowWiFi"
                    checked={currentPolicy.deviceFeatures?.allowWiFi ?? true}
                    onChange={(e) => handleNestedFieldChange('deviceFeatures', 'allowWiFi', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="allowWiFi">
                    Allow Wi-Fi Settings
                  </label>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="mb-2">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="allowGPS"
                    checked={currentPolicy.deviceFeatures?.allowGPS ?? true}
                    onChange={(e) => handleNestedFieldChange('deviceFeatures', 'allowGPS', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="allowGPS">
                    Allow GPS / Location
                  </label>
                </div>
              </div>

              <div className="mb-2">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="allowNFC"
                    checked={currentPolicy.deviceFeatures?.allowNFC ?? true}
                    onChange={(e) => handleNestedFieldChange('deviceFeatures', 'allowNFC', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="allowNFC">
                    Allow NFC
                  </label>
                </div>
              </div>

              <div className="mb-2">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="allowUSB"
                    checked={currentPolicy.deviceFeatures?.allowUSB ?? false}
                    onChange={(e) => handleNestedFieldChange('deviceFeatures', 'allowUSB', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="allowUSB">
                    Allow USB File Transfer
                  </label>
                </div>
              </div>
            </div>
          </div>

          <hr />

          <h6 className="mb-3">
            <MdSettings className="me-2" />
            Kiosk Mode
          </h6>

          <div className="mb-2">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="kioskEnabled"
                checked={currentPolicy.kioskMode?.enabled ?? false}
                onChange={(e) => handleNestedFieldChange('kioskMode', 'enabled', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="kioskEnabled">
                Enable Kiosk Mode (Lock device to POS app)
              </label>
            </div>
          </div>

          {currentPolicy.kioskMode?.enabled && (
            <>
              <div className="mb-2">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="hideStatusBar"
                    checked={currentPolicy.kioskMode?.hideStatusBar ?? false}
                    onChange={(e) => handleNestedFieldChange('kioskMode', 'hideStatusBar', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="hideStatusBar">
                    Hide Status Bar
                  </label>
                </div>
              </div>

              <div className="mb-2">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="hideNavigationBar"
                    checked={currentPolicy.kioskMode?.hideNavigationBar ?? false}
                    onChange={(e) => handleNestedFieldChange('kioskMode', 'hideNavigationBar', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="hideNavigationBar">
                    Hide Navigation Bar
                  </label>
                </div>
              </div>

              <div className="mb-3">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="disableHomeButton"
                    checked={currentPolicy.kioskMode?.disableHomeButton ?? false}
                    onChange={(e) => handleNestedFieldChange('kioskMode', 'disableHomeButton', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="disableHomeButton">
                    Disable Home Button
                  </label>
                </div>
              </div>
            </>
          )}

          <hr />

          <h6 className="mb-3">
            <MdSettings className="me-2" />
            App Updates
          </h6>

          <div className="alert alert-info mb-3" role="alert">
            <strong>ℹ️ Update Delivery:</strong> Updates are automatically deployed via CI/CD pipeline. 
            <ul className="mb-0 mt-2">
              <li><strong>Stable:</strong> Production releases (Google Play production track)</li>
              <li><strong>Beta:</strong> Pre-release testing (Google Play beta track)</li>
              <li><strong>Dev:</strong> Internal testing (Google Play alpha/internal tracks)</li>
            </ul>
            Devices check for updates every 6 hours when auto-update is enabled.
          </div>

          <div className="mb-2">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="autoUpdate"
                checked={currentPolicy.updates.autoUpdate}
                onChange={(e) => handleNestedFieldChange('updates', 'autoUpdate', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="autoUpdate">
                Enable Automatic Updates
              </label>
            </div>
            <small className="form-text text-muted">
              When enabled, devices will automatically check for and install updates from the selected channel.
            </small>
            
            <div className="alert alert-info mt-2 mb-0" style={{ fontSize: '0.85rem' }}>
              <strong>📦 Distribution Flavor:</strong>
              <ul className="mb-0 mt-1" style={{ fontSize: '0.8rem' }}>
                <li><strong>Enterprise APK</strong> (Firebase Storage): Supports MDM auto-update ✅</li>
                <li><strong>Play Store AAB</strong> (Google Play): Updates via Play Store only ⚠️</li>
              </ul>
              <small className="text-muted">
                Auto-update only works with enterprise builds distributed via Firebase Storage.
                Play Store devices will ignore this setting and update through Google Play.
              </small>
            </div>
          </div>

          <div className="mb-2">
            <label className="form-label">Update Channel</label>
            <select
              className="form-select"
              value={currentPolicy.updates.updateChannel}
              onChange={(e) => handleNestedFieldChange('updates', 'updateChannel', e.target.value)}
            >
              <option value="stable">Stable (Production Releases)</option>
              <option value="beta">Beta (Pre-release Testing)</option>
              <option value="dev">Development (Internal Testing)</option>
            </select>
            <small className="form-text text-muted">
              Devices will receive updates from the selected channel. Stable is recommended for production devices.
            </small>
          </div>

          <div className="mb-2">
            <label className="form-label">Maintenance Window</label>
            <select
              className="form-select"
              value={currentPolicy.updates.maintenanceWindow}
              onChange={(e) => handleNestedFieldChange('updates', 'maintenanceWindow', e.target.value)}
            >
              <option value="anytime">Anytime (Install Immediately)</option>
              <option value="night">Night (12 AM - 6 AM)</option>
              <option value="manual">Manual Only (No Auto-Install)</option>
            </select>
            <small className="form-text text-muted">
              Controls when automatic updates can be installed. Night mode is recommended to avoid disrupting operations.
            </small>
          </div>

          <div className="mb-3">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="allowBetaFeatures"
                checked={currentPolicy.updates.allowBetaFeatures}
                onChange={(e) => handleNestedFieldChange('updates', 'allowBetaFeatures', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="allowBetaFeatures">
                Allow Beta Features
              </label>
            </div>
          </div>

          <hr />

          <h6 className="mb-3">
            <MdSecurity className="me-2" />
            Device Restrictions
          </h6>

          <div className="mb-2">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="allowFactoryReset"
                checked={currentPolicy.deviceRestrictions?.allowFactoryReset ?? false}
                onChange={(e) => handleNestedFieldChange('deviceRestrictions', 'allowFactoryReset', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="allowFactoryReset">
                Allow Factory Reset
              </label>
            </div>
          </div>

          <div className="mb-2">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="allowUSBDebugging"
                checked={currentPolicy.deviceRestrictions?.allowUSBDebugging ?? false}
                onChange={(e) => handleNestedFieldChange('deviceRestrictions', 'allowUSBDebugging', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="allowUSBDebugging">
                Allow USB Debugging
              </label>
            </div>
          </div>

          <div className="mb-2">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="allowAppInstallation"
                checked={currentPolicy.deviceRestrictions?.allowAppInstallation ?? false}
                onChange={(e) => handleNestedFieldChange('deviceRestrictions', 'allowAppInstallation', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="allowAppInstallation">
                Allow App Installation from Unknown Sources
              </label>
            </div>
          </div>

          <div className="mb-3">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="allowUninstallApps"
                checked={currentPolicy.deviceRestrictions?.allowUninstallApps ?? false}
                onChange={(e) => handleNestedFieldChange('deviceRestrictions', 'allowUninstallApps', e.target.checked)}
              />
              <label className="form-check-label" htmlFor="allowUninstallApps">
                Allow Uninstalling System Apps
              </label>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSavePolicy}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default PoliciesView;
